/**
 * Page propre d'un produit : `/shop/p/<nom>-<ref>` (18/10/2026).
 *
 * Avant, une fiche n'était qu'une fenêtre ouverte sur le catalogue
 * (`/shop?produit=<id>`) : Google y voyait le catalogue entier et rangeait les
 * ~1 150 fiches comme doublons de /shop. Ici, la fiche occupe la page, sous un
 * petit en-tête de retour. Le serveur a déjà écrit le titre, la description et
 * un contenu lisible avant le chargement (voir api `page-produit-html.ts`) ;
 * cette page prend le relais pour le visiteur.
 *
 * Le pied de page légal et l'adresse canonique viennent de `ShopFooterLayout`.
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ShoppingBag } from 'lucide-react';
import ProductDetailModal from '../../components/ecommerce/products/ProductDetailModal';
import { ecommerceApi } from '../../api/ecommerce.api';
import { useTitrePage } from '../../hooks/useTitrePage';
import { useEcommerceCart } from '../../hooks/ecommerce/useEcommerceCart';

export default function ProduitPage() {
  const { segment = '' } = useParams<{ segment: string }>();
  const navigate = useNavigate();
  const [fiche, setFiche] = useState<{ id: string | null; nom: string | null } | null>(null);
  const itemCount = useEcommerceCart((s) => s.getItemCount());
  useTitrePage(fiche?.nom || null);

  useEffect(() => {
    let actif = true;
    setFiche(null);
    ecommerceApi.shop
      .produitParAdresse(segment)
      .then((r) => {
        if (!actif) return;
        // Produit renommé : on se range à l'adresse à jour, sans ajouter d'historique.
        if (r.chemin && r.chemin !== `/shop/p/${segment}`) navigate(r.chemin, { replace: true });
        setFiche({ id: r.id, nom: r.nom });
      })
      .catch(() => actif && setFiche({ id: null, nom: null }));
    return () => {
      actif = false;
    };
  }, [segment, navigate]);

  return (
    <div className="min-h-screen bg-[#faf7f0]">
      <header className="shop-sans bg-white border-b border-black/[.08] sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-3">
          <Link to="/shop" className="flex items-center gap-2 text-[#3a3324] font-semibold hover:text-green-800" aria-label="Retour à la boutique">
            <ArrowLeft className="w-5 h-5" />
            Boutique
          </Link>
          <span className="flex-1 text-center font-bold text-[#3a3324] truncate">Les Folies Maraîchères</span>
          <Link to="/shop/cart" className="relative p-2 text-[#3a3324]" aria-label="Panier">
            <ShoppingBag className="w-6 h-6" />
            {Number(itemCount) > 0 && (
              <span className="absolute -top-0.5 -right-0.5 min-w-5 h-5 px-1 rounded-full bg-[#b45f2e] text-white text-xs font-bold flex items-center justify-center">
                {itemCount}
              </span>
            )}
          </Link>
        </div>
      </header>

      {fiche === null ? null : fiche.id ? (
        <ProductDetailModal productId={fiche.id} mode="page" onClose={() => navigate('/shop')} />
      ) : (
        <div className="shop-sans max-w-xl mx-auto px-4 py-16 text-center">
          <p className="text-lg text-gray-700">Ce produit n'est plus proposé pour le moment.</p>
          <Link to="/shop" className="mt-4 inline-block underline text-green-800 font-semibold">Voir la boutique</Link>
        </div>
      )}
    </div>
  );
}
